import { type ReactNode, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useUserContext } from '../context/UserContext';
import { captureRespondentMessage } from '../utils/report-respondent-error';

/**
 * Minimal layout for form respondents (submit pages).
 * No admin header, no navigation — just auth guard + content.
 * Renders `children` when used as a wrapper, else the nested <Outlet />.
 */
export default function RespondentLayout({ children }: { children?: ReactNode }) {
  const { isLoading, user, authError, initAuth, retryAuth } = useUserContext();
  const { formId } = useParams<{ formId: string }>();

  useEffect(() => {
    if (authError) {
      captureRespondentMessage('auth', authError, { formId, statusCode: 401 });
    }
  }, [authError, formId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-ink-tertiary animate-spin" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="font-display text-xl font-bold text-ink">Authentication failed</h1>
          <p className="text-ink-secondary text-sm mt-2 mb-6">{authError}</p>
          <button onClick={retryAuth} className="btn-primary w-full">Try Again</button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="font-display text-xl font-bold text-ink mb-2">Sign in required</h1>
          <p className="text-ink-secondary text-sm mb-6">Please sign in to access this form.</p>
          <button onClick={initAuth} className="btn-primary w-full py-3">Sign In</button>
        </div>
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
